/**
 * CSS styles extracted from novela-share for pixel-perfect preview rendering.
 *
 * Sources:
 * - novela-share/src/styles/theme.ts (Joy theme palette)
 * - novela-share/src/styles/typographyTheme.ts (Typography scale)
 * - novela-share/src/app/article/[articleId]/layout.tsx
 * - novela-share/src/app/article/[articleId]/(free)/FreeArticleView.tsx
 * - novela-share/src/app/article/[articleId]/(pro)/ProArticleView.tsx
 * - novela-share/src/app/article/[articleId]/(public)/PublicArticleView.tsx
 * - novela-share/src/app/article/[articleId]/components/ArticleContentViewer.tsx
 * - novela-share/src/app/article/[articleId]/(free)/components/FreePaywallOverlay.tsx
 * - novela-share/src/app/article/[articleId]/(public)/components/PublicPaywallOverlay.tsx
 * - novela-share/src/app/article/[articleId]/components/AuthorProfile.tsx
 */

// ─── Font Configuration ───

const FONT_BASE_URL = import.meta.env.VITE_FONT_BASE_URL || "";

/**
 * Google Fonts CDN URL — single combined link for all available fonts.
 * Browser only downloads font binaries for families actually used in content.
 */
export const GOOGLE_FONTS_URL =
  "https://fonts.googleapis.com/css2?" +
  [
    "family=Gothic+A1:wght@400;700",
    "family=Gowun+Batang:wght@400;700",
    "family=Nanum+Gothic:wght@400;700",
    "family=Nanum+Myeongjo:wght@400;700",
    "family=Noto+Sans+KR:wght@400;700",
    "family=Noto+Serif+KR:wght@400;700",
    "family=Roboto:wght@400;700",
    "family=Open+Sans:wght@400;700",
    "family=Lato:wght@400;700",
    "family=Montserrat:wght@400;700",
    "family=Inter:wght@400;700",
    "family=Nunito+Sans:wght@400;700",
    "family=Raleway:wght@400;700",
    "family=Work+Sans:wght@400;700",
    "family=Noto+Sans:wght@400;700",
    "family=Noto+Serif:wght@400;700",
    "family=Cormorant+Garamond:wght@400;700",
    "family=Libre+Baskerville:wght@400;700",
    "family=Tinos:wght@400;700",
    "family=Playfair+Display:wght@400;700",
  ].join("&") +
  "&display=swap";

/** Pretendard Variable from jsDelivr CDN */
export const PRETENDARD_URL =
  "https://cdn.jsdelivr.net/gh/orioncactus/pretendard@v1.3.9/dist/web/variable/pretendardvariable-dynamic-subset.min.css";

/**
 * @font-face declarations for fonts not on Google Fonts or with name mismatches.
 * These only trigger downloads when font-family is actually used in rendered content.
 */
export const CUSTOM_FONT_FACES = `
  @font-face {
    font-family: "RIDIBatang";
    src: url('${FONT_BASE_URL}RIDIBatang.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "SeoulNamsan";
    src: url('${FONT_BASE_URL}SeoulNamsanM.woff') format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "SeoulNamsan";
    src: url('${FONT_BASE_URL}SeoulNamsanB.woff') format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "HamChoRomBatang";
    src: url('${FONT_BASE_URL}HamChoRomBatang-Medium.woff') format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "HamChoRomBatang";
    src: url('${FONT_BASE_URL}HamChoRomBatang-Bold.woff') format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "GmarketSans";
    src: url('${FONT_BASE_URL}GmarketSansTTFLight.woff') format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "GmarketSans";
    src: url('${FONT_BASE_URL}GmarketSansTTFBold.woff') format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "KoPubWorld-Dotum";
    src: url('${FONT_BASE_URL}KoPubWorld-Dotum-Medium.woff') format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "KoPubWorld-Dotum";
    src: url('${FONT_BASE_URL}KoPubWorld-Dotum-Bold.woff') format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "KoPubWorld-Batang";
    src: url('${FONT_BASE_URL}KoPubWorld-Batang-Medium.woff') format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "KoPubWorld-Batang";
    src: url('${FONT_BASE_URL}KoPubWorld-Batang-Bold.woff') format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "Google Sans";
    src: url('${FONT_BASE_URL}Google-Sans-Regular.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "Google Sans";
    src: url('${FONT_BASE_URL}Google-Sans-Bold.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "MaruBuri";
    src: url('${FONT_BASE_URL}MaruBuri-Regular.woff') format('woff');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "MaruBuri";
    src: url('${FONT_BASE_URL}MaruBuri-Bold.woff') format('woff');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "Libre Caslon";
    src: url('${FONT_BASE_URL}Libre-Caslon-Regular.woff2') format('woff2');
    font-weight: 400;
    font-style: normal;
    font-display: swap;
  }
  @font-face {
    font-family: "Libre Caslon";
    src: url('${FONT_BASE_URL}Libre-Caslon-Bold.woff2') format('woff2');
    font-weight: 700;
    font-style: normal;
    font-display: swap;
  }
`;

const FONT_FAMILY = [
  "'Pretendard Variable'",
  "Pretendard",
  "-apple-system",
  "BlinkMacSystemFont",
  "system-ui",
  "Roboto",
  "'Helvetica Neue'",
  "'Segoe UI'",
  "'Apple SD Gothic Neo'",
  "'Noto Sans KR'",
  "'Malgun Gothic'",
  "'Apple Color Emoji'",
  "'Segoe UI Emoji'",
  "'Segoe UI Symbol'",
  "sans-serif",
].join(", ");

export const SHARE_PREVIEW_CSS = `
/* ─── Reset ─── */
*, *::before, *::after {
  box-sizing: border-box;
  margin: 0;
  padding: 0;
}

/* ─── Joy Theme CSS Custom Properties ─── */
:root {
  /* Primary */
  --joy-palette-primary-50: #E0F2ED;
  --joy-palette-primary-100: #B3DFD1;
  --joy-palette-primary-200: #82CBB3;
  --joy-palette-primary-300: #52B696;
  --joy-palette-primary-400: #2EA682;
  --joy-palette-primary-500: #11966F;
  --joy-palette-primary-600: #0D8963;
  --joy-palette-primary-700: #077955;
  --joy-palette-primary-800: #006948;
  --joy-palette-primary-900: #004D2D;
  --joy-palette-primary-solidBg: #11966F;
  --joy-palette-primary-solidHoverBg: #0D8963;

  /* Neutral */
  --joy-palette-neutral-50: #fafafa;
  --joy-palette-neutral-100: #f6f6f6;
  --joy-palette-neutral-200: #f0f0f0;
  --joy-palette-neutral-300: #e2e2e2;
  --joy-palette-neutral-400: #c0c0c0;
  --joy-palette-neutral-500: #a1a1a1;
  --joy-palette-neutral-600: #787878;
  --joy-palette-neutral-700: #636363;
  --joy-palette-neutral-750: #545454;
  --joy-palette-neutral-800: #444444;
  --joy-palette-neutral-900: #232323;

  /* Text */
  --joy-palette-text-primary: rgba(0, 0, 0, 0.8);
  --joy-palette-text-secondary: rgba(0, 0, 0, 0.65);
  --joy-palette-text-tertiary: rgba(0, 0, 0, 0.55);
  --joy-palette-text-icon: rgba(0, 0, 0, 0.38);
  --joy-palette-text-placeholder: rgba(0, 0, 0, 0.28);

  /* Gray (alpha-based) */
  --joy-palette-gray-100: rgba(0, 0, 0, 0.03);
  --joy-palette-gray-200: rgba(0, 0, 0, 0.07);
  --joy-palette-gray-300: rgba(0, 0, 0, 0.09);
  --joy-palette-gray-400: rgba(0, 0, 0, 0.14);
  --joy-palette-gray-500: rgba(0, 0, 0, 0.2);
  --joy-palette-gray-600: rgba(0, 0, 0, 0.28);
  --joy-palette-gray-700: rgba(0, 0, 0, 0.38);
  --joy-palette-gray-800: rgba(0, 0, 0, 0.55);
  --joy-palette-gray-900: rgba(0, 0, 0, 0.65);
  --joy-palette-gray-1000: rgba(0, 0, 0, 0.8);
  --joy-palette-gray-1100: rgba(0, 0, 0, 0.87);

  /* Background */
  --joy-palette-background-body: #FFFFFF;
  --joy-palette-background-surface: #FAFAFA;

  /* Common */
  --joy-palette-common-white: #FFFFFF;
  --joy-palette-common-black: #000000;
}

/* ─── Base ─── */
html {
  -webkit-text-size-adjust: 100%;
}

body {
  font-family: ${FONT_FAMILY};
  background-color: #FFF9F0;
  margin: 0;
  padding: 0;
  min-height: 100vh;
  color: var(--joy-palette-text-tertiary);
  letter-spacing: -0.02em;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

a {
  text-decoration: none;
  color: inherit;
}

img {
  display: block;
  max-width: 100%;
}

/* ─── Typography Levels ─── */
.typo-h4-semibold {
  font-size: 20px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 133%;
}

.typo-title1-bold {
  font-size: 18px;
  font-weight: 700;
  letter-spacing: -0.02em;
  line-height: 133%;
}

.typo-title2-medium {
  font-size: 16px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 133%;
}

.typo-title3-semibold {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 133%;
}

.typo-body1-medium {
  font-size: 15px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 150%;
}

.typo-body1-regular {
  font-size: 15px;
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 150%;
}

.typo-body2-regular {
  font-size: 14px;
  font-weight: 400;
  letter-spacing: -0.02em;
  line-height: 150%;
}

/* ─── Text Colors ─── */
.text-primary { color: var(--joy-palette-text-primary); }
.text-secondary { color: var(--joy-palette-text-secondary); }
.text-tertiary { color: var(--joy-palette-text-tertiary); }
.text-white { color: var(--joy-palette-common-white); }
.text-primary-500 { color: var(--joy-palette-primary-500); }
.text-neutral-50 { color: var(--joy-palette-neutral-50); }

/* ─── Header (58px) ─── */
.article-header {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 58px;
  padding: 0 20px;
  max-width: 1440px;
  margin: 0 auto;
  background-color: rgba(255, 249, 240, 0.75);
  backdrop-filter: blur(16px);
  -webkit-backdrop-filter: blur(16px);
  border-bottom: 1px solid transparent;
}

.article-header__title {
  font-size: 14px;
  font-weight: 600;
  letter-spacing: -0.02em;
  line-height: 133%;
  color: var(--joy-palette-text-primary);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 300px;
}

/* ─── Article Layout (main) ─── */
.article-layout {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding-top: 100px;
  padding-bottom: 300px;
  height: calc(100dvh - 58px);
  overflow: auto;
}

@media (max-width: 768px) {
  .article-layout {
    padding-top: 28px;
    padding-bottom: 0;
  }
}

/* ─── Thumbnail ─── */
.thumbnail-container {
  /* default: no extra padding */
}

@media (max-width: 768px) {
  .thumbnail-container {
    padding-left: 10px;
    padding-right: 10px;
  }
}

.thumbnail {
  border-radius: 8px;
  width: 320px;
  height: 180px;
  object-fit: cover;
}

/* ─── Content Header ─── */
.content-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  width: 100%;
  max-width: 740px;
  padding-top: 42px;
  padding-bottom: 42px;
}

@media (max-width: 768px) {
  .content-header {
    padding-left: 10px;
    padding-right: 10px;
  }
}

.article-title {
  font-size: 36px;
  font-weight: 600;
  line-height: 133%;
  text-align: center;
  color: var(--joy-palette-text-primary);
}

@media (max-width: 768px) {
  .article-title {
    font-size: 24px;
    letter-spacing: -0.48px;
  }
}

.content-meta {
  display: flex;
  align-items: center;
}

.meta-separator {
  color: var(--joy-palette-gray-500);
  margin: 0 4px;
  font-size: 12px;
}

/* ─── Content Container ─── */
.content-container {
  width: 100%;
  max-width: 740px;
  margin: 0 auto;
  margin-bottom: 16px;
}

.content-container--with-overlay {
  position: relative;
  width: 100%;
  max-width: 740px;
  margin: 0 auto;
  margin-bottom: 16px;
}

.content-container--public {
  position: relative;
  width: 100%;
  height: auto;
  max-width: 740px;
  margin: 0 auto;
  margin-bottom: 16px;
}

@media (max-width: 768px) {
  .content-container,
  .content-container--with-overlay,
  .content-container--public {
    margin-bottom: 0;
  }
}

/* ─── ArticleContentViewer ─── */
.article-content-viewer {
  width: 100%;
  height: 100%;
  padding: 36px;
  line-height: 2;
  margin: 0;
  color: rgba(0, 0, 0, 0.8);
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid var(--joy-palette-gray-200);
}

.article-content-viewer p {
  margin-bottom: 5pt;
  text-indent: 24px;
}

@media (max-width: 768px) {
  .article-content-viewer {
    padding: 28px 20px;
    border-bottom: none;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

/* ─── Paywall Overlay (Shared) ─── */
.paywall-overlay {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: 100%;
  height: 340px;
  border-radius: 8px;
  border: 1px solid transparent;
  border-top: none;
}

@media (max-width: 768px) {
  .paywall-overlay {
    height: 363px;
  }
}

.paywall-blur {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  width: 100%;
  height: 100px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(1px);
  -webkit-backdrop-filter: blur(1px);
}

.paywall-content {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background-color: rgba(255, 255, 255, 1);
  mask-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 1) 20%,
    rgba(255, 255, 255, 1) 60%,
    rgba(255, 255, 255, 1) 100%
  );
  -webkit-mask-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 1) 20%,
    rgba(255, 255, 255, 1) 60%,
    rgba(255, 255, 255, 1) 100%
  );
  width: 100%;
  height: 100%;
  padding: 62px 48px;
  padding-top: 140px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.paywall-heading {
  margin-bottom: 8px;
}

.paywall-description {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 20px;
}

.paywall-buttons {
  display: flex;
  align-items: center;
  gap: 8px;
}

@media (max-width: 768px) {
  .paywall-buttons--public {
    flex-direction: column;
  }
}

/* ─── Paywall Buttons ─── */
.paywall-btn-subscribe {
  background-color: var(--joy-palette-primary-solidBg);
  border-radius: 6px;
  padding: 10px 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  cursor: pointer;
  min-width: 177px;
  height: 40px;
}

.paywall-btn-subscribe:hover {
  background-color: var(--joy-palette-primary-solidHoverBg);
}

.paywall-btn-login {
  background-color: var(--joy-palette-neutral-750);
  border-radius: 6px;
  padding: 10px 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  border: none;
  cursor: pointer;
  min-width: 131px;
  height: 40px;
}

.paywall-btn-login:hover {
  background-color: var(--joy-palette-neutral-600);
}

@media (max-width: 768px) {
  .paywall-btn-login {
    padding: 10px 52px;
  }
}

/* ─── Author Profile ─── */
.author-profile-container {
  width: 100%;
  max-width: 740px;
  margin: 0 auto;
  margin-bottom: 48px;
}

@media (max-width: 768px) {
  .author-profile-container {
    padding: 19px 20px 0 20px;
    margin-bottom: 0;
    background-color: white;
  }
}

.author-profile {
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  max-width: 740px;
  background-color: white;
  border: 1px solid var(--joy-palette-gray-200);
  border-radius: 9px;
  padding: 28px 20px;
}

@media (max-width: 768px) {
  .author-profile {
    border-radius: 0;
    border-right: none;
    border-left: none;
    padding-left: 0;
    padding-right: 0;
    padding-top: 19px;
    padding-bottom: 47px;
  }
}

.profile-image {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 58px;
  height: 58px;
  border-radius: 50%;
  background-color: var(--joy-palette-gray-200);
  flex-shrink: 0;
  overflow: hidden;
}

.profile-image img {
  border-radius: 50%;
  width: 58px;
  height: 58px;
  object-fit: cover;
}

.profile-content {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}

.profile-name-row {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.profile-divider {
  width: 1px;
  height: 20px;
  background-color: var(--joy-palette-gray-300);
  align-self: center;
  margin: 0 3px;
}

.social-icons {
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.social-link {
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  color: inherit;
}

.social-link:hover {
  opacity: 0.8;
}

.social-icon {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.social-icon svg {
  width: 100%;
  height: 100%;
}

/* ─── Default Profile Icon (SVG placeholder) ─── */
.profile-icon-placeholder {
  width: 32px;
  height: 32px;
  color: var(--joy-palette-gray-500);
}

/* ─── Copyright ─── */
.copyright-container {
  /* desktop: no extra styles */
}

@media (max-width: 768px) {
  .copyright-container {
    width: 100%;
    display: flex;
    justify-content: center;
    background-color: white;
    padding: 47px 0 300px 0;
  }
}

.copyright-text {
  font-family: 'Gowun Batang', serif;
  font-size: 14px;
  font-weight: 700;
  line-height: 14px;
  letter-spacing: -0.28px;
  color: var(--joy-palette-text-tertiary);
  cursor: pointer;
}

.copyright-text:hover {
  color: var(--joy-palette-primary-500);
}
`;
